import { useMemo, useRef, useState } from "react";

import { useVirtualizer } from "@tanstack/react-virtual";

import { AdminAreaHierarchy } from "@/types/adminArea";

import SearchBox from "../ui/searchBox";
import { TreeNode } from "./TreeNode";

const TEST_ID_PREFIX = "searchbox-area-filter";

type Props = {
  hierarchyData?: AdminAreaHierarchy[];
  selectedAreaIds: string[];
  explicitSelectedAreaIds: string[];
  setSelectedIds: (ids: string[]) => void;
  setExplicitSelectedIds: (ids: string[], id: string) => void;
};

const ROW_HEIGHT = 24;

export const AdminAreaTreeSelection = ({
  hierarchyData,
  selectedAreaIds,
  explicitSelectedAreaIds,
  setSelectedIds,
  setExplicitSelectedIds
}: Props) => {
  const selectedIds = useMemo(
    () => new Set(selectedAreaIds),
    [selectedAreaIds]
  );
  const explicitSelectedIds = useMemo(
    () => new Set(explicitSelectedAreaIds),
    [explicitSelectedAreaIds]
  );

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const data: AdminAreaHierarchy[] = useMemo(() => {
    if (!hierarchyData) return [];
    return hierarchyData;
  }, [hierarchyData]);

  const findNodeById = (
    nodes: AdminAreaHierarchy[],
    id: string
  ): AdminAreaHierarchy | null => {
    for (const n of nodes) {
      if (n.id === id) return n;
      if (n.sub_areas) {
        const found = findNodeById(n.sub_areas, id);
        if (found) return found;
      }
    }
    return null;
  };

  // expand toggle manual
  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // collect expanded Ids
  const collect = (nodes: AdminAreaHierarchy[], allIds: Set<string>) => {
    for (const n of nodes) {
      allIds.add(n.id);
      if (n.sub_areas) collect(n.sub_areas, allIds);
    }
  };

  // expand/collapse all
  const handleExpandAll = (expand: boolean) => {
    const allIds = new Set<string>();
    collect(data, allIds);
    setExpandedIds(expand ? allIds : new Set());
  };

  const flattenTreeIds = (nodes: AdminAreaHierarchy[]): string[] => {
    const result: string[] = [];

    const stack = [...nodes];
    while (stack.length) {
      const node = stack.pop()!;
      result.push(node.id);
      if (node.sub_areas) {
        stack.push(...node.sub_areas);
      }
    }

    return result;
  };

  const allNodeIds = useMemo(() => flattenTreeIds(data), [data]);

  const allExpanded = useMemo(() => {
    return allNodeIds.length > 0 && expandedIds.size === allNodeIds.length;
  }, [expandedIds, allNodeIds]);

  // helper find parent chain
  const findParentChain = (
    nodes: AdminAreaHierarchy[],
    targetId: string
  ): string[] => {
    const stack: string[] = [];

    // Depth-First Search
    const dfs = (ns: AdminAreaHierarchy[], path: string[]): boolean => {
      for (const n of ns) {
        const newPath = [...path, n.id];
        if (n.id === targetId) {
          stack.push(...path);
          return true;
        }
        if (n.sub_areas && dfs(n.sub_areas, newPath)) return true;
      }
      return false;
    };

    dfs(nodes, []);
    return stack;
  };

  // helper find parent
  const findParent = (
    nodes: AdminAreaHierarchy[],
    targetId: string
  ): AdminAreaHierarchy | null => {
    for (const n of nodes) {
      if (n.sub_areas?.some((c) => c.id === targetId)) return n;
      const found = n.sub_areas && findParent(n.sub_areas, targetId);
      if (found) return found;
    }
    return null;
  };

  const getChildIds = (node: AdminAreaHierarchy) => {
    return (
      node.sub_areas?.flatMap((c) => [
        c.id,
        ...(c.sub_areas ?? []).map((cc) => cc.id)
      ]) ?? []
    );
  };

  // select logic
  const handleToggleSelect = (
    checked: boolean,
    node: AdminAreaHierarchy,
    allChildren: string[]
  ) => {
    const nextSelected = new Set(selectedIds);
    const nextExplicit = new Set(explicitSelectedIds);

    const addAllToSelected = (ids: string[]) => {
      for (const id of ids) nextSelected.add(id);
    };

    const removeAllFromSelected = (ids: string[]) => {
      for (const id of ids) nextSelected.delete(id);
      nextExplicit.delete(node.id);
    };

    const removeExplicitDescendants = (n: AdminAreaHierarchy) => {
      if (!n.sub_areas) return;
      for (const c of n.sub_areas) {
        nextExplicit.delete(c.id);
        if (c.sub_areas) removeExplicitDescendants(c);
      }
    };

    const cleanupExplicitParents = (nodes: AdminAreaHierarchy[]) => {
      for (const n of nodes) {
        if (!n.sub_areas) continue;
        cleanupExplicitParents(n.sub_areas);

        const childIds = getChildIds(n);
        const hasSelectedChild = childIds.some((cid) => nextSelected.has(cid));
        if (nextExplicit.has(n.id) && !hasSelectedChild)
          nextExplicit.delete(n.id);
      }
    };

    const updateParentSelection = (
      childId: string,
      nodes: AdminAreaHierarchy[]
    ) => {
      let parent = findParent(nodes, childId);
      while (parent) {
        const allChecked =
          parent.sub_areas?.every((c) => nextSelected.has(c.id)) ?? false;
        if (allChecked) nextSelected.add(parent.id);
        else nextSelected.delete(parent.id);
        parent = findParent(nodes, parent.id);
      }
    };

    const handleSelect = () => {
      const hasChildren = allChildren.length > 1; // first element = self, others = descendants
      if (hasChildren) {
        const childIds = allChildren.filter((id) => id !== node.id);
        const someChildSelected = childIds.some((cid) => nextSelected.has(cid));
        const allChildSelected = childIds.every((cid) => nextSelected.has(cid));

        if (someChildSelected && !allChildSelected) {
          for (const cid of childIds) nextExplicit.delete(cid);
          nextExplicit.add(node.id);
        }
      }

      addAllToSelected(allChildren);

      const parentIds = findParentChain(data, node.id);
      const hasExplicitParent = parentIds.some((pid) => nextExplicit.has(pid));
      if (!hasExplicitParent) nextExplicit.add(node.id);
    };

    const handleUnselect = () => {
      removeAllFromSelected(allChildren);
      const fullNode = findNodeById(data, node.id);
      if (fullNode) removeExplicitDescendants(fullNode);
      cleanupExplicitParents(data);
    };

    if (checked) handleSelect();
    else handleUnselect();

    updateParentSelection(node.id, data);

    // create temp for unsaved changes
    setSelectedIds([...nextSelected]);
    setExplicitSelectedIds([...nextExplicit], node.id);
  };

  // filter tree + auto-expand parent
  const { filteredData, autoExpandedIds } = useMemo(() => {
    if (!searchTerm.trim()) {
      return { filteredData: data, autoExpandedIds: expandedIds };
    }

    const lower = searchTerm.toLowerCase();
    const expanded = new Set(expandedIds);

    const filterRecursive = (
      nodes: AdminAreaHierarchy[]
    ): AdminAreaHierarchy[] => {
      const result: AdminAreaHierarchy[] = [];

      for (const n of nodes) {
        const selfMatch = n.name.toLowerCase().includes(lower);
        const childrenFiltered = n?.sub_areas?.length
          ? filterRecursive(n.sub_areas)
          : [];

        if (selfMatch) {
          // Parent matches → show all children without auto-expanding
          result.push({ ...n });
        } else if (childrenFiltered.length > 0) {
          // Child matches → show parent and auto-expand it
          expanded.add(n.id);
          result.push({ ...n, sub_areas: childrenFiltered });
        }
      }
      return result;
    };

    const filtered = filterRecursive(data);
    return { filteredData: filtered, autoExpandedIds: expanded };
  }, [searchTerm, data, expandedIds]);

  const selectedCount = explicitSelectedIds.size;

  const isIndeterminate = (node: AdminAreaHierarchy) => {
    const children = node.sub_areas ?? [];

    if (children.length === 0) return false;

    let selectedCount = 0;
    for (const c of children) {
      if (selectedIds.has(c.id)) selectedCount++;
    }

    return (
      selectedCount > 0 &&
      selectedCount < children.length &&
      !selectedIds.has(node.id)
    );
  };

  const parentRef = useRef<HTMLDivElement>(null);
  const visibleRows = useMemo(() => {
    const rows: { node: AdminAreaHierarchy; depth: number }[] = [];

    const walk = (nodes: AdminAreaHierarchy[], depth: number) => {
      for (const n of nodes) {
        rows.push({ node: n, depth });

        if (autoExpandedIds.has(n.id) && n.sub_areas) {
          walk(n.sub_areas, depth + 1);
        }
      }
    };

    walk(filteredData, 0);
    return rows;
  }, [filteredData, autoExpandedIds]);

  const rowVirtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    measureElement: (el) => el.getBoundingClientRect().height
  });

  return (
    <div>
      <SearchBox
        disabled={false}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeHolder="Search"
        className="max-w-xs py-3 my-4 text-[12px]"
        testId={TEST_ID_PREFIX}
      />

      <div className="flex items-center justify-between">
        <span aria-label={`Selected Area count: ${selectedCount}`}>
          Selected Area ({selectedCount})
        </span>
        <button
          className="text-primary-teal text-xs font-medium hover:underline"
          onClick={() => handleExpandAll(!allExpanded)}
        >
          {allExpanded ? "Collapse All" : "Expand All"}
        </button>
      </div>

      <div
        ref={parentRef}
        className="overflow-y-auto max-h-[calc(100vh-395px)] relative mt-4"
      >
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: "relative"
          }}
        >
          {rowVirtualizer.getVirtualItems().map((v) => {
            const row = visibleRows[v.index];

            return (
              <div
                key={row.node.id}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  transform: `translateY(${v.start}px)`,
                  width: "100%"
                }}
              >
                <TreeNode
                  node={row.node}
                  depth={row.depth}
                  isExpanded={autoExpandedIds.has(row.node.id)}
                  isSelected={selectedIds.has(row.node.id)}
                  isIndeterminate={isIndeterminate(row.node)}
                  onToggleExpand={handleToggleExpand}
                  onToggleSelect={handleToggleSelect}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
